
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const SettingsPage: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'sync' | 'schema'>('idle');

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setDisplayName(user.user_metadata?.full_name || '');
    };
    fetchUser();
  }, []);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await supabase.auth.updateUser({ data: { full_name: displayName } });
      alert('System: Administrator identity synchronized.');
    } catch (err) {
      alert('System: Error synchronizing identity.');
    } finally {
      setSaving(false);
    }
  };

  const forceSchemaSync = async () => {
    setSyncing(true);
    try {
      const { error } = await supabase.rpc('reload_schema');
      if (error) throw error;
      alert('PostgREST: API Cache invalidated. Global schema refreshed.');
    } catch (err: any) {
      console.error(err);
      alert('Sync Failed: Ensure you have executed the reload_schema function in SQL first.');
    } finally {
      setSyncing(false);
    }
  };

  const fullSchemaSQL = `-- EVERNX WORLD-CLASS SCHEMA BLUEPRINT

-- 1. SETUP STORAGE (Run this, then create a public bucket named 'music' in dashboard)
-- INSERT INTO storage.buckets (id, name, public) VALUES ('music', 'music', true);

-- 2. CORE MUSIC REPOSITORY
CREATE TABLE IF NOT EXISTS public.songs (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    title TEXT NOT NULL,
    artist TEXT NOT NULL,
    album TEXT,
    genre TEXT,
    file_url TEXT NOT NULL,
    cover_url TEXT,
    duration INTEGER DEFAULT 0,
    bpm INTEGER,
    release_year INTEGER DEFAULT extract(year from now()),
    lyrics TEXT,
    status TEXT DEFAULT 'published' CHECK (status IN ('draft', 'published', 'archived', 'flagged')),
    is_explicit BOOLEAN DEFAULT false,
    is_featured BOOLEAN DEFAULT false,
    allow_download BOOLEAN DEFAULT true,
    allow_streaming BOOLEAN DEFAULT true,
    play_count INTEGER DEFAULT 0,
    download_count INTEGER DEFAULT 0,
    like_count INTEGER DEFAULT 0,
    uploaded_by UUID REFERENCES auth.users(id),
    created_at TIMESTAMPTZ DEFAULT now(),
    updated_at TIMESTAMPTZ DEFAULT now()
);

-- 3. COLLECTION REPOSITORY
CREATE TABLE IF NOT EXISTS public.playlists (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    name TEXT NOT NULL,
    description TEXT,
    cover_url TEXT,
    visibility TEXT DEFAULT 'public' CHECK (visibility IN ('public', 'private', 'unlisted')),
    is_collaborative BOOLEAN DEFAULT false,
    song_count INTEGER DEFAULT 0,
    play_count INTEGER DEFAULT 0,
    follower_count INTEGER DEFAULT 0,
    created_by UUID REFERENCES auth.users(id),
    created_at TIMESTAMPTZ DEFAULT now(),
    updated_at TIMESTAMPTZ DEFAULT now()
);

-- 4. JUNCTION ARCHITECTURE
CREATE TABLE IF NOT EXISTS public.playlist_songs (
    playlist_id UUID REFERENCES public.playlists(id) ON DELETE CASCADE,
    song_id UUID REFERENCES public.songs(id) ON DELETE CASCADE,
    position INTEGER,
    added_by UUID REFERENCES auth.users(id),
    added_at TIMESTAMPTZ DEFAULT now(),
    PRIMARY KEY (playlist_id, song_id)
);

-- 5. AD BANNER SYSTEM
CREATE TABLE IF NOT EXISTS public.ads (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    title TEXT NOT NULL,
    description TEXT,
    ad_type TEXT DEFAULT 'image' CHECK (ad_type IN ('image', 'html')),
    image_url TEXT,
    html_code TEXT,
    redirect_url TEXT,
    advertiser_name TEXT,
    position TEXT DEFAULT 'header' CHECK (position IN ('header', 'sidebar', 'footer', 'popup')),
    size TEXT DEFAULT '728x90',
    background_color TEXT DEFAULT '#1e293b',
    text_color TEXT DEFAULT '#ffffff',
    status TEXT DEFAULT 'active' CHECK (status IN ('active', 'paused', 'expired')),
    start_date TIMESTAMPTZ DEFAULT now(),
    end_date TIMESTAMPTZ,
    priority INTEGER DEFAULT 1,
    impressions INTEGER DEFAULT 0,
    clicks INTEGER DEFAULT 0,
    created_at TIMESTAMPTZ DEFAULT now(),
    updated_at TIMESTAMPTZ DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.ad_clicks (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    ad_id UUID REFERENCES public.ads(id) ON DELETE CASCADE,
    user_agent TEXT,
    ip_address TEXT,
    clicked_at TIMESTAMPTZ DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.ad_impressions (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    ad_id UUID REFERENCES public.ads(id) ON DELETE CASCADE,
    user_agent TEXT,
    ip_address TEXT,
    viewed_at TIMESTAMPTZ DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_ads_position_status ON public.ads(position, status);

-- 6. AUTOMATED COUNTER TRIGGER
CREATE OR REPLACE FUNCTION update_playlist_song_count()
RETURNS TRIGGER AS $$
BEGIN
    IF (TG_OP = 'INSERT') THEN
        UPDATE public.playlists SET song_count = song_count + 1 WHERE id = NEW.playlist_id;
    ELSIF (TG_OP = 'DELETE') THEN
        UPDATE public.playlists SET song_count = song_count - 1 WHERE id = OLD.playlist_id;
    END IF;
    RETURN NULL;
END;
$$ LANGUAGE plpgsql;

DROP TRIGGER IF EXISTS tr_update_playlist_song_count ON public.playlist_songs;
CREATE TRIGGER tr_update_playlist_song_count
AFTER INSERT OR DELETE ON public.playlist_songs
FOR EACH ROW EXECUTE FUNCTION update_playlist_song_count();

-- 7. API CACHE SYNC FUNCTION (Fixes PostgREST caching)
CREATE OR REPLACE FUNCTION reload_schema()
RETURNS void LANGUAGE plpgsql SECURITY DEFINER
AS $$ BEGIN EXECUTE 'NOTIFY pgrst, ''reload schema'''; END; $$;`;

  const copyToClipboard = (text: string, state: 'sync' | 'schema') => {
    navigator.clipboard.writeText(text);
    setCopyState(state);
    setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-1000 pb-32">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-white/5 pb-10 gap-6">
        <div>
          <span className="text-accent-cyan font-black uppercase tracking-[0.5em] text-[10px] mb-2 block">Infrastructure Nerve Center</span>
          <h2 className="text-6xl font-black text-white uppercase tracking-tighter leading-none">Settings</h2>
        </div>
        <div className="flex items-center space-x-6">
           <div className="text-right">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">System Load</span>
              <p className="text-green-500 font-black text-xl">Minimal</p>
           </div>
           <div className="w-[1px] h-10 bg-white/10"></div>
           <div className="text-right">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Admin Level</span>
              <p className="text-accent-blue font-black text-xl tracking-tight uppercase">Super-User</p>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-10">
        {/* Force Sync Controller */}
        <div className="xl:col-span-1 space-y-8">
          <div className="glass p-10 rounded-[3.5rem] border-accent-blue/20 relative overflow-hidden group shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-50"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-3xl bg-accent-blue/10 flex items-center justify-center text-accent-blue mb-8 border border-accent-blue/20">
                <i className="fas fa-bolt-lightning text-2xl"></i>
              </div>
              <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-4">PostgREST Sync</h3>
              <p className="text-slate-400 text-sm font-medium leading-relaxed mb-10">
                Instantly invalidate the API cache. Use this after running SQL migrations to ensure the portal sees new columns and tables immediately.
              </p>
              
              <button 
                onClick={forceSchemaSync}
                disabled={syncing}
                className={`w-full py-6 rounded-3xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center space-x-4 shadow-3xl ${
                  syncing ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-accent-blue hover:bg-accent-blue/80 text-white active:scale-95'
                }`}
              >
                <i className={`fas fa-rotate ${syncing ? 'animate-spin' : ''}`}></i>
                <span>{syncing ? 'Syncing...' : 'Force Refresh'}</span>
              </button>
            </div>
          </div>

          <div className="glass p-10 rounded-[3.5rem] border-white/5 shadow-2xl">
             <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] mb-8">Node Diagnostics</h4>
             <div className="space-y-6">
                {[
                  { label: 'Database Node', status: 'Healthy', color: 'text-green-500' },
                  { label: 'API Gateway', status: 'Ready', color: 'text-green-500' },
                  { label: 'Storage Cluster', status: 'Active', color: 'text-accent-cyan' },
                ].map((stat, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-400">{stat.label}</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${stat.color}`}>{stat.status}</span>
                  </div>
                ))}
             </div>
          </div>
        </div>

        {/* SQL Blueprint Section */}
        <div className="xl:col-span-2 glass p-12 rounded-[4rem] border-white/5 shadow-3xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent-purple/5 rounded-full blur-[120px] -mr-48 -mt-48"></div>
          
          <div className="flex items-center justify-between mb-12 relative z-10">
            <div className="flex items-center space-x-6">
              <div className="w-14 h-14 rounded-2xl bg-accent-purple/10 flex items-center justify-center text-accent-purple border border-accent-purple/20">
                <i className="fas fa-terminal text-xl"></i>
              </div>
              <div>
                <h3 className="text-3xl font-black text-white uppercase tracking-tighter">Schema Blueprint</h3>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Full SQL Logic</p>
              </div>
            </div>
            <button 
              onClick={() => copyToClipboard(fullSchemaSQL, 'schema')}
              className="bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-white/5 transition-all active:scale-95"
            >
              {copyState === 'schema' ? 'Blueprint Copied' : 'Copy Blueprint'}
            </button>
          </div>

          <div className="relative group rounded-[2.5rem] overflow-hidden border border-white/5">
            <div className="bg-[#010411] p-8 font-mono text-[11px] text-slate-400 h-[450px] overflow-y-auto custom-scrollbar leading-relaxed">
              {fullSchemaSQL.split('\n').map((line, i) => (
                <div key={i} className="flex">
                  <span className="w-10 text-slate-800 select-none mr-4">{i + 1}</span>
                  <span className={line.startsWith('--') ? 'text-slate-600 italic' : line.startsWith('CREATE') ? 'text-accent-purple' : 'text-slate-300'}>{line}</span>
                </div>
              ))}
            </div>
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#010411] to-transparent pointer-events-none"></div>
          </div>

          <div className="mt-10 p-6 bg-accent-blue/5 border border-accent-blue/10 rounded-3xl flex items-center space-x-6">
            <i className="fas fa-circle-info text-accent-blue text-xl"></i>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest leading-loose">
              Executing this script in the Supabase SQL Editor establishes the core logic of the EvernX ecosystem.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
